// ── 휴일 관리 섹션 ──────────────────────────────────────────────────────────
// 1. 시스템 공휴일 (공공데이터 — 읽기 전용, 전체 공용 배지)
// 2. 회사 휴일 (개업일, 창립기념일 등 — 추가/삭제 가능)
// PayrollSettings.jsx에서 사용

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { stdBlackBtn } from '../adminlayout/designStandard';

// ── 스타일 상수 ──────────────────────────────────────────────────────────────
const cardStyle = {
  background: 'var(--bg-card)', borderRadius: 12, border: '1px solid var(--border)',
  boxShadow: 'none', padding: '16px 20px',
};
const inputStyle = {
  padding: '7px 10px', borderRadius: 7, border: '1px solid var(--border)',
  fontSize: 12, background: 'var(--bg-card)', color: 'var(--text)', boxSizing: 'border-box', outline: 'none',
};
const btnSmall = {
  border: 'none', borderRadius: 6, padding: '5px 12px',
  fontSize: 11, fontWeight: 700, cursor: 'pointer',
};
// 배지 — 디자인 표준 톤 (활성=연파랑+파랑 / 보조=회색)
const badgeSystem = {
  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
  background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)', marginLeft: 6,
};
const badgeCompany = {
  fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
  background: 'var(--border)', color: 'var(--text-sub)', marginLeft: 6,
};

export default function HolidaySection({ companyCode, showToast }) {
  // ── 상태 ───────────────────────────────────────────────────────────────────
  const [publicHolidays, setPublicHolidays] = useState([]);
  const [companyHolidays, setCompanyHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPublic, setShowPublic] = useState(false); // 시스템 공휴일 펼치기/접기
  const [year, setYear] = useState(new Date().getFullYear());

  // 추가 폼
  const [newDate, setNewDate] = useState('');
  const [newName, setNewName] = useState('');
  const [newRecurring, setNewRecurring] = useState(false);
  const [saving, setSaving] = useState(false);

  // ── 데이터 로드 ────────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    const [pubRes, comRes] = await Promise.all([
      apiClient.get(`/api/admin/payroll/public-holidays?year=${year}`),
      apiClient.get(`/api/admin/payroll/company-holidays?company_code=${companyCode}`),
    ]);
    if (pubRes.ok) setPublicHolidays(pubRes.data?.data || []);
    if (comRes.ok) setCompanyHolidays(comRes.data?.data || []);
    setLoading(false);
  }, [companyCode, year]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── 회사 휴일 추가 ────────────────────────────────────────────────────────
  const handleAdd = async () => {
    if (!newDate || !newName.trim()) { showToast('날짜와 이름을 입력하세요.', 'info'); return; }
    setSaving(true);
    const res = await apiClient.post('/api/admin/payroll/company-holidays', {
      company_code: companyCode, ymd: newDate, name: newName.trim(), is_recurring: newRecurring,
    });
    setSaving(false);
    if (res.ok) {
      showToast('휴일이 등록되었습니다.', 'success');
      setNewDate(''); setNewName(''); setNewRecurring(false);
      fetchData();
    } else {
      showToast(res.data?.message || '등록 실패', 'error');
    }
  };

  // ── 회사 휴일 삭제 ────────────────────────────────────────────────────────
  const handleDelete = async (id) => {
    if (!window.confirm('이 휴일을 삭제하시겠습니까?')) return;
    const res = await apiClient.delete(`/api/admin/payroll/company-holidays/${id}`);
    if (res.ok) { showToast('삭제 완료', 'success'); fetchData(); }
    else showToast(res.data?.message || '삭제 실패', 'error');
  };

  if (loading) return <div style={{ ...cardStyle, textAlign: 'center', color: 'var(--text-muted)' }}>휴일 로딩 중...</div>;

  return (
    <div style={cardStyle}>
      <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--text-sub)', marginBottom: 12 }}>
        휴일 관리
      </div>

      {/* ── 시스템 공휴일 (접기/펼치기) ───────────────────────────────────── */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <button
            onClick={() => setShowPublic(!showPublic)}
            style={{ ...btnSmall, background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)' }}
          >
            {showPublic ? '▼' : '▶'} 시스템 공휴일 ({year}년)
          </button>
          <span style={badgeSystem}>전체 공용</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>공공데이터 자동 수집 · 읽기 전용</span>
          {/* 연도 선택 */}
          <select
            style={{ ...inputStyle, width: 80, marginLeft: 'auto' }}
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
          >
            {[year - 1, year, year + 1, year + 2].map((y) => (
              <option key={y} value={y}>{y}년</option>
            ))}
          </select>
        </div>
        {showPublic && (
          <div style={{ background: 'var(--bg-subtle)', borderRadius: 8, padding: '8px 12px', border: '1px solid var(--border)' }}>
            {publicHolidays.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{year}년 공휴일 데이터가 없습니다.</div>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {publicHolidays.map((h) => (
                  <span key={h.ymd} style={{ fontSize: 11, background: 'var(--std-badge-bg, rgba(59,154,242,0.12))', color: 'var(--std-badge-text, #1d7de6)', borderRadius: 4, padding: '3px 8px', fontWeight: 600 }}>
                    {h.ymd.slice(5)} {h.name}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── 회사 휴일 ────────────────────────────────────────────────────── */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text-sub)' }}>회사 휴일</span>
          <span style={badgeCompany}>회사별</span>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>급여 지급일 휴일 판단에 반영</span>
        </div>

        {/* 목록 */}
        {companyHolidays.length === 0 ? (
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>등록된 회사 휴일이 없습니다.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
            {companyHolidays.map((h) => (
              <div key={h.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', background: 'var(--bg-subtle)', borderRadius: 6, border: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text)', minWidth: 80 }}>{h.ymd.slice(0, 10)}</span>
                <span style={{ fontSize: 12, color: 'var(--text-sub)', flex: 1 }}>{h.name}</span>
                {h.is_recurring && (
                  <span style={{ fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 3, background: 'rgba(16,185,129,0.1)', color: '#059669' }}>매년 반복</span>
                )}
                <button onClick={() => handleDelete(h.id)} style={{ ...btnSmall, background: 'rgba(220,38,38,0.08)', color: '#dc2626' }}>삭제</button>
              </div>
            ))}
          </div>
        )}

        {/* 추가 폼 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input type="date" style={{ ...inputStyle, width: 140 }} value={newDate} onChange={(e) => setNewDate(e.target.value)} />
          <input style={{ ...inputStyle, width: 160 }} value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="휴일 이름 (예: 창립기념일)" />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, color: 'var(--text-sub)', cursor: 'pointer' }}>
            <input type="checkbox" checked={newRecurring} onChange={(e) => setNewRecurring(e.target.checked)} />
            매년 반복
          </label>
          <button onClick={handleAdd} disabled={saving} style={{ ...stdBlackBtn, padding: '5px 12px', fontSize: 11 }}>
            {saving ? '등록 중...' : '+ 추가'}
          </button>
        </div>
      </div>
    </div>
  );
}
